import { z } from "zod/v4-mini";

import { FileModel, fileSchema } from "@/models/FileModel.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

type BaseExtendedUser = {
  id: string;
  email: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
};

export type PatientUser = BaseExtendedUser & {
  userType: "patient";
  firstName: string;
  lastName: string;
};

export type BasePractitionerUser = {
  id: string;
  firstName: string;
  lastName: string;
  qualification: "doctor" | "nurse";
  profilePicture?: FileModel;
};

export type PractitionerUser = BaseExtendedUser &
  BasePractitionerUser & {
    userType: "practitioner";
  };

export type AdminUser = BaseExtendedUser & {
  userType: "admin";
};

export type ExtendedUser = PatientUser | PractitionerUser | AdminUser;

export const basePractitionerUserSchema: Schema<BasePractitionerUser> = cz.camelCaseObject({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  qualification: z.literal(["doctor", "nurse"]),
  profile_picture: cz.optional(fileSchema),
});

export const extendedUserSchema: Schema<ExtendedUser> = cz.camelCase(
  z.intersection(
    cz.camelCaseObject({
      id: z.string(),
      email: z.string(),
      last_login: cz.optional(z.string()),
      created_at: z.string(),
      updated_at: z.string(),
    }),
    z.discriminatedUnion("user_type", [
      cz.camelCaseObject({
        user_type: z.literal("patient"),
        first_name: z.string(),
        last_name: z.string(),
      }),
      cz.camelCaseObject({
        user_type: z.literal("practitioner"),
        first_name: z.string(),
        last_name: z.string(),
        qualification: z.literal(["doctor", "nurse"]),
        profile_picture: cz.optional(fileSchema),
      }),
      cz.camelCaseObject({
        user_type: z.literal("admin"),
      }),
    ]),
  ),
);

export const userFormattedName = (familyName: string | undefined, givenName: string | undefined) =>
  familyName && givenName ? `${familyName} ${givenName}`.capitalizeAll().trim() : "";

export const patientFormattedName = (familyName: string | undefined, givenName: string | undefined) =>
  familyName && givenName ? `${givenName} ${familyName}`.capitalizeAll().trim() : "";

export const doctorFormattedName = (familyName: string | undefined, givenName: string | undefined) =>
  familyName && givenName ? `${givenName.at(0)?.toUpperCase()}. ${familyName.capitalizeAll().trim()}` : "";

export const isPractitionerUser = (user: ExtendedUser): user is PractitionerUser => user.userType === "practitioner";
